import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';

import { AuthService } from '../../../../core/services/auth.service';
import { sanitizeReturnUrl } from '../../../../shared/utils/url.util';
import { TitleService } from '../../../../core/services/title.service';
import { AuthProvider } from '../../../../shared/models/user/user.enums';
import { NotificationService } from '../../../../core/services/notification.service';
import { AppLoadingOverlayComponent } from '../../../../shared/components/loading-overlay/loading-overlay.component';
import {
	AUTH_PROVIDER_LABELS,
	AUTH_RETURN_URL_SESSION_STORAGE_KEY,
	ExternalAuthProvider,
	parseExternalAuthProvider
} from '../../../../shared/models/auth/auth.constants';
import {
	LinkAccountCandidate,
	SelectLinkAccountDialogComponent
} from '../../../../shared/components/select-link-account-dialog/select-link-account-dialog.component';

@Component({
	selector: 'app-external-auth-callback',
	templateUrl: './external-auth-callback.component.html',
	imports: [AppLoadingOverlayComponent, SelectLinkAccountDialogComponent],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalAuthCallbackComponent implements OnInit {
	public isLinking = signal(false);
	public showSelectAccountDialog = signal(false);
	public candidates = signal<LinkAccountCandidate[]>([]);
	public providerLabel = signal<string>(AUTH_PROVIDER_LABELS[AuthProvider.AZURE]);

	private linkToken: string | null = null;
	private linkProvider: ExternalAuthProvider = AuthProvider.AZURE;

	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);
	private readonly authService = inject(AuthService);
	private readonly titleService = inject(TitleService);
	private readonly notificationService = inject(NotificationService);

	ngOnInit(): void {
		this.titleService.setTitle('Authentication');
		this.handleCallback();
	}

	/**
	 * Handles OAuth callback: processes token, error, or account selection from query params.
	 */
	private handleCallback(): void {
		const queryParams = new URLSearchParams(window.location.search);
		const token = queryParams.get('token');
		const error = queryParams.get('error');
		const action = queryParams.get('action');

		if (error) {
			this.handleAuthError('Authentication failed', error);
			return;
		}

		if (action === 'select') {
			this.handleAccountSelection(queryParams);
			return;
		}

		if (token) {
			this.handleTokenAuth(token);
		} else {
			this.handleAuthError('Authentication failed', 'No token or error received');
		}
	}

	/**
	 * Handles token authentication flow.
	 *
	 * @param {string} token - The access token.
	 */
	private handleTokenAuth(token: string): void {
		this.authService
			.handleExternalAuthCallback(token)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.notificationService.success('Signed in successfully!');
					this.navigateToReturnUrl();
				},
				error: (errorResponse) => {
					this.handleAuthError(errorResponse?.error?.message || 'Authentication failed', errorResponse?.error?.error);
				}
			});
	}

	/**
	 * Handles account selection flow when multiple users found.
	 *
	 * @param {URLSearchParams} queryParams - The URL query parameters.
	 */
	private handleAccountSelection(queryParams: URLSearchParams): void {
		this.linkToken = queryParams.get('linkToken');
		const candidatesJson = queryParams.get('candidates');
		const provider = queryParams.get('provider');
		this.linkProvider = parseExternalAuthProvider(provider);
		this.providerLabel.set(AUTH_PROVIDER_LABELS[this.linkProvider]);

		if (!this.linkToken || !candidatesJson) {
			this.handleAuthError('Authentication failed', 'Missing selection data');
			return;
		}

		try {
			const parsedCandidates = JSON.parse(decodeURIComponent(candidatesJson));
			this.candidates.set(parsedCandidates);
			this.showSelectAccountDialog.set(true);
		} catch {
			this.handleAuthError('Authentication failed', 'Invalid selection data');
		}
	}

	/**
	 * Handles account selection from dialog.
	 *
	 * @param {LinkAccountCandidate} candidate - The selected candidate.
	 */
	public onAccountSelected(candidate: LinkAccountCandidate): void {
		if (!this.linkToken) {
			this.handleAuthError('Authentication failed', 'Link token missing');
			return;
		}

		this.isLinking.set(true);
		const link$ = this.authService.linkAccount(this.linkToken!, candidate.id, this.linkProvider);

		link$.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.showSelectAccountDialog.set(false);
					this.notificationService.success('Account linked successfully!');
					this.navigateToReturnUrl();
				},
				error: (errorResponse) => {
					this.isLinking.set(false);
					this.notificationService.error('Failed to link account', errorResponse?.error?.message || 'Please try again');
				}
			});
	}

	/**
	 * Handles selection cancellation from dialog.
	 */
	public onSelectionCancelled(): void {
		this.clearReturnUrl();
		void this.router.navigate(['/auth/sign-in']);
	}

	/**
	 * Handles auth error: clears returnUrl, shows notification, redirects to sign-in.
	 *
	 * @param {string} message - The error message.
	 * @param {string} detail - The error detail.
	 */
	private handleAuthError(message: string, detail?: string): void {
		this.clearReturnUrl();
		this.notificationService.error(message, detail);
		void this.router.navigate(['/auth/sign-in']);
	}

	/**
	 * Navigates to sanitized returnUrl from sessionStorage or home page.
	 */
	private navigateToReturnUrl(): void {
		const returnUrl = sanitizeReturnUrl(sessionStorage.getItem(AUTH_RETURN_URL_SESSION_STORAGE_KEY));
		this.clearReturnUrl();
		void this.router.navigateByUrl(returnUrl);
	}

	/**
	 * Clears stored returnUrl from sessionStorage.
	 */
	private clearReturnUrl(): void {
		sessionStorage.removeItem(AUTH_RETURN_URL_SESSION_STORAGE_KEY);
	}
}
